import styled from '@emotion/styled';
import { colors, fontSizes, shape, spacing } from '../utils';

/* TYPES */

interface ChartLegendProps {
  align?: string;
}

interface LegendItem {
  color: string;
  dot?: boolean;
  key: string;
  line?: boolean;
  round?: boolean;
  strokeDasharray?: string | number;
  strokeWidth?: number;
}

type ChartLegendShapeProps = Pick<LegendItem, 'round'>;

interface ChartLegendLineProps
  extends Pick<LegendItem, 'dot' | 'strokeDasharray' | 'strokeWidth'> {
  stroke: string;
}

export interface LegendProps {
  align?: string;
  legendData?: LegendItem[];
}

/* STYLES */

const ChartLegend = styled.div<ChartLegendProps>`
  display: flex;
  flex-flow: wrap;
  justify-content: ${(props) => props.align};
  padding: ${spacing.medium};
  width: 100%;
`;

const ChartLegendItem = styled.div`
  align-content: center;
  align-items: center;
  display: flex;
  flex-direction: row;
  height: 28px;
  margin-left: ${spacing.medium};

  &:first-of-type {
    margin-left: 0;
  }
`;

const ChartLegendShape = styled.div<ChartLegendShapeProps>`
  background: ${(props) => props.color};
  border-radius: ${(props) => (props.round ? '50%' : shape.borderRadius)};
  height: ${spacing.medium};
  width: ${spacing.medium};
`;

const ChartLegendLabel = styled.div`
  color: ${colors.text.primary};
  font-size: ${fontSizes.small};
  margin-left: ${spacing.xsmall};
`;

const ChartLegendLine = ({
  dot,
  stroke,
  strokeDasharray,
  strokeWidth,
}: ChartLegendLineProps) => (
  <svg
    height="16px"
    version="1.1"
    viewBox="0 0 16 16"
    xmlns="http://www.w3.org/2000/svg"
    xmlnsXlink="http://www.w3.org/1999/xlink"
    width="16px"
  >
    <title>chart-legend-item-line</title>
    <line
      x1="0"
      y1="8"
      x2="16"
      y2="8"
      stroke={stroke}
      strokeWidth={strokeWidth}
      strokeDasharray={strokeDasharray}
    />
    {dot && <circle cx="8" cy="8" fill={stroke} r="2.5" stroke={stroke} />}
  </svg>
);

export const Legend = ({ align = 'flex-end', legendData }: LegendProps) => {
  if (!legendData) {
    return null;
  }

  return (
    <ChartLegend align={align}>
      {legendData.map((data) => (
        <ChartLegendItem key={data.key}>
          {data.line ? (
            <ChartLegendLine
              dot={data.dot}
              stroke={data.color}
              strokeWidth={data.strokeWidth}
              strokeDasharray={data.strokeDasharray}
            />
          ) : (
            <ChartLegendShape color={data.color} round={data.round} />
          )}
          <ChartLegendLabel>{data.key}</ChartLegendLabel>
        </ChartLegendItem>
      ))}
    </ChartLegend>
  );
};
